import { Injectable, OnDestroy, Optional } from '@angular/core';
import { ParamMap } from '@angular/router';

import { Observable, of, Subscription, BehaviorSubject, forkJoin,
    combineLatest, iif, tap, throwError, merge, EMPTY } from 'rxjs';
import { catchError, finalize, filter, map, switchMap, take, shareReplay } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { DiscoverRouteModel } from '../../DiscoverRouter/models/discover-route.model';
import { DiscoverRoutingService } from '../../DiscoverRouter/services/discover-routing.service';
import { DiscoverRouteTypeEnum } from '../../DiscoverRouter/enums/discover-route-type.enum';
import { TargetingSearch } from '../models/targeting-search.model';
import { TargetingSearchList } from '../models/targeting-search-list.model';
import { TargetingSearchesApiService } from '../../../api/targeting/targeting-searches.api.service';
import { TargetingSearchService } from './targeting-search.service';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { SearchModeEnum } from '../../../Common/TargetingShared/enum/search-mode.enum';
import { OutletModel } from '../models/outlet.model';
import { JournodexApiService } from '../../../api/journodex/journodex.api.service';
import { ContactModel } from '../../../Common/TargetingShared/model/contact.model';
import { StatusFilterService } from '../components/TargetingResults/StatusFilter/status-filter.service';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { IdNamePairModel } from '../../../Common/Misc/models/id-name-pair.model';
import { OutletsPublicApi } from '../../../api/targeting/outlets-public.api';
import { ContactsPublicApi } from '../../../api/targeting/contacts-public.api';
import { TargetingSearchApi } from '../../../api/targeting/model/targeting-search.api.model';
import { JournodexEntityFactory } from '../factories/journodex-entity.factory';
import { WebsocketAwareNeptuneService, ResourceType } from '../../../Bootstrap/services/websocket-aware-neptune.service';
import { IncludeExcludeModel } from '../../../Common/Misc/models/include-exclude-model';
import { JournodexSubjectsService } from '../../../Common/Journodex/services/journodex-subjects.service';
import { FilterListProviderServiceInterface } from '../../../Common/Filter/services/filter-list-provider-service.interface';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { UserPreferencesService } from '../../../Account/UserPreferences/services/user-preferences.service';
import { UserAppTargetingPreferencesModel } from '../../../Core/Users/models/user-app-preferences.model';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';
import { DistributionApi } from '../../../api/distributions/distribution.api';
import { DistributionSummaryModel } from '../../../Common/TargetingShared/model/distribution-summary-model';
import { JournodexEnumsService } from '../../../Discover/Core/services/journodex-enums.service';
import { NewResourceDialogModel } from '../../../Common/Dialogs/models/new-resource-dialog.model';
import { NewResourceDialogComponent } from '../../../Common/Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';
import { CurrentResourceAbstractService } from '@Common/Basic/service/current-resource-abstract.service';
import { TargetingSearchListApi } from '../../../api/targeting/model/targeting-search-list.api.model';
import { MultiBaseAbstractApi } from '../../../api/genericApi/multi-base-abstract.api';
import { ResourceAction } from '@Common/Basic/service/resource-abstract.service';
import { ResourceActionTypes } from '@Common/Basic/enums/resource-actions.enum';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '@Core/Users/enums/user-permissions.enum';
import { CommonApiErrorEnum } from '@api/genericApi/enums/common-api-error.enum';
import { DisplayGroupsService } from '@Common/Layout/services/display-groups.service';
import { PendoService } from '../../../Common/Misc/services/pendo.service';
import { TargetingSearchPendoEventEnum } from '../enums/targeting-search-pendo-event.enum';

type DisplaySettings = UserAppTargetingPreferencesModel['savedSearchesPrefs']['defaultSearchSettings'];

@Injectable()
export class TargetingSearchesService extends CurrentResourceAbstractService<TargetingSearch, TargetingSearchApi, TargetingSearchList, TargetingSearchListApi>
    implements FilterListProviderServiceInterface, OnDestroy {

    all$: Observable<TargetingSearchList[]> = this.currentUserStateService.hasAnyPermissions([userPermissionsEnum.advancedTargeting]).pipe(
        switchMap((hasPermission) =>
            hasPermission ? merge(
                this.getApiService().getList().pipe(
                    map(list => {
                        // This filter shouldn't be needed, but sometimes the BE sends back the filter searches as searches with no type
                        return list
                            .filter(apiModel => apiModel.query.searchMode)
                            .map(item => this.constructListModel(item));
                    }),
                ),
                this.refreshAllManualSubject,
            ) : of([])
        ),
        shareReplay(1)
    );

    currentResource$: Observable<TargetingSearch> = this.currentResourceSubject.asObservable().pipe(
        switchMap(search => {
            return forkJoin([
                this.getAllChildrenSubjectsOfIncludeExclude(search?.subjects),
                this.getAllChildrenSubjectsOfIncludeExclude(search?.outletSubjects)
            ]).pipe(
                switchMap(([subjects, outletSubjects]) => {
                    if (search) {
                        search.subjects = subjects;
                        search.outletSubjects = outletSubjects;
                    }
                    return of(search);
                })
            );
        }),
        switchMap(search => {
            return this.mapContactOutletIdsToNames(search);
        }),
        tap(resource => {
            if (resource) {
                this.cachedResource = resource.clone();
                this.statusFilterService.setContactTypeFromSearch(resource.includePrivateContacts);
            } else {
                this.cachedResource = null;
            }
        }),
        shareReplay(1)
    );

    // Used to disable the route guard temporarily because the
    // "new search" button already showed the exit dialog
    creatingNewSearch: boolean = false;

    protected subscriptions: Subscription[] = [];

    private currentRoute: DiscoverRouteModel;

    private previewCount$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    private previewCountLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private distributions: DistributionSummaryModel[] = [];
    private distributions$: BehaviorSubject<DistributionSummaryModel[]> = new BehaviorSubject(this.distributions);
    private unavailableOutletSubject = new BehaviorSubject<boolean>(false);
    unavailableOutlet$ = this.unavailableOutletSubject.asObservable();

    constructor(
        private discoverRoutingService: DiscoverRoutingService,
        private targetingSearchesApiService: TargetingSearchesApiService,
        private messageService: MessageService,
        private targetingSearchService: TargetingSearchService,
        private journodexApiService: JournodexApiService,
        private statusFilterService: StatusFilterService,
        public outletApi: OutletsPublicApi,
        public contactApi: ContactsPublicApi,
        private journodexEntityFactory: JournodexEntityFactory,
        private websocketEvents: WebsocketAwareNeptuneService,
        private journodexSubjectsService: JournodexSubjectsService,
        private userPreferencesService: UserPreferencesService,
        public distributionApi: DistributionApi,
        private journodexEnumService: JournodexEnumsService,
        private currentUserStateService: CurrentUserStateService,
        private displayGroupsService: DisplayGroupsService,
        @Optional() private pendoService: PendoService,
    ) {
        super();

        this.subscriptions.push(
            combineLatest([
                this.discoverRoutingService.getCurrentRoute(),
                this.userPreferencesService.getUserAppPreferences().pipe(
                    map(userAppPrefs => userAppPrefs.targetingPrefs.savedSearchesPrefs.defaultSearchSettings)
                )
            ]).pipe(
                switchMap(([route, searchSettings]) => {
                    return this.onUpdateRoute(route, searchSettings);
                })
            ).subscribe(),
            this.websocketEvents.getWebsocketMessages().pipe(
                filter(x => !!x),
                switchMap(message => {
                    if (
                        message.resourceType === ResourceType.targetingSearches &&
                        message.slug &&
                        message.slug.indexOf('targetingsearches/search') === -1 &&
                        message.slug.indexOf('count') === -1
                    ) {
                        return this.refreshAllManual().pipe(
                            filter(x => !!x),
                            take(1),
                        );
                    }

                    return of(null);
                })
            ).subscribe(),
            this.distributionApi.getList().subscribe(
                (summaries) => {
                    this.distributions = summaries.map(summary => new DistributionSummaryModel(summary));
                    this.distributions$.next(this.distributions);
                }
            )
        );
    }

    override refreshAllManual(): Observable<TargetingSearchList[]> {
        return this.getApiService().getList().pipe(
            map(list => {
                return list
                    .filter(apiModel => apiModel.query.searchMode)
                    .map(item => this.constructListModel(item));
            }),
            tap((list) => this.refreshAllManualSubject.next(list))
        );
    }

    getApiService(): MultiBaseAbstractApi<TargetingSearchApi, TargetingSearchListApi> {
        return this.targetingSearchesApiService;
    }

    constructResourceModel(apiModel: TargetingSearchApi): TargetingSearch {
        return new TargetingSearch(apiModel);
    }

    constructListModel(listApiModel: TargetingSearchListApi): TargetingSearchList {
        return new TargetingSearchList(listApiModel);
    }

    messageCenter(actionResult: ResourceAction) {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource as TargetingSearch;

        switch(actionType) {
            case ResourceActionTypes.CREATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.dialogs.saveSearch.error.summary',
                        detail: this.localizationService.transform('Discover.dialogs.saveSearch.error.detail', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Discover.dialogs.saveSearch.saveMessageSummary',
                        detail: this.localizationService.transform('Discover.dialogs.saveSearch.saveMessage', {
                            name: resource.name
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.UPDATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.dialogs.updateSearch.error.summary',
                        detail: this.localizationService.transform('Discover.dialogs.updateSearch.error.detail', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Discover.dialogs.saveSearch.saveMessageSummary',
                        detail: this.localizationService.transform('Discover.dialogs.saveSearch.saveMessage', {
                            name: resource.name
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.RENAME: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.targeting.alerts.renameSearchError.summary',
                        detail: 'Discover.targeting.alerts.renameSearchError.detail'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Discover.targeting.alerts.renameSearchSuccess.summary',
                        detail: this.localizationService.transform('Discover.targeting.alerts.renameSearchSuccess.detail', {
                            name: resource.name
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.DELETE: {
                if (error) {
                    this.messageService.add({
                        severity : messageSeverityEnum.error,
                        detail: 'Discover.confirmDialog.deleteSearch.error'
                    });
                } else {
                    this.messageService.add({
                        severity : messageSeverityEnum.success,
                        detail: 'Discover.confirmDialog.deleteSearch.success'
                    });
                }
                break;
            }
            case ResourceActionTypes.BULK_DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.confirmDialog.bulkDeleteSearch.error.summary',
                        detail: 'Discover.confirmDialog.bulkDeleteSearch.error.detail'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Discover.confirmDialog.bulkDeleteSearch.success.summary',
                        detail: this.localizationService.transform('Discover.confirmDialog.bulkDeleteSearch.success.detail', {
                            number: actionResult.count
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.BULK_VISIBILITY: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.bulkUpdateSavedSearch.error.summary',
                        detail: 'Discover.bulkUpdateSavedSearch.error.detail'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Discover.bulkUpdateSavedSearch.success.summary',
                        detail: this.localizationService.transform('Discover.bulkUpdateSavedSearch.success.detail', {
                            number: actionResult.count
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.COPY: {
                if (error) {
                    this.messageService.add({
                        severity : messageSeverityEnum.error,
                        summary: 'Discover.copySearch',
                        detail: 'Discover.confirmDialog.copy.error'
                    });
                } else {
                    this.messageService.add({
                        severity : messageSeverityEnum.success,
                        summary: 'Discover.copySearch',
                        detail: 'Discover.confirmDialog.copy.success'
                    });
                }

                break;
            }
        }
    }

    create(search: TargetingSearch, group?: DisplayGroupModel): Observable<number> {
        return this.createResource(search).pipe(
            switchMap(
                (searchId) => {
                    return this.refreshAllManual().pipe(
                        filter(x => !!x),
                        take(1),
                        map(x => searchId)
                    );
                }
            ),
            switchMap((id) => {
                if (group) {
                    return this.updateGroup([id], group).pipe(
                        map(x => id)
                    );
                } else {
                    return of(id);
                }
            }),
        );
    }

    /**
     * Fires a Pendo event capturing which filters were populated on a search, e.g. when it's run or saved.
     * Tracking errors should not cause the caller's following code to fail.
     */
    trackSearchEvent(type: TargetingSearchPendoEventEnum, search: TargetingSearch): void {
        try {
            this.pendoService?.trackEvent({
                type,
                data: {
                    searchId: search.id ?? null,
                    searchMode: search.searchMode,
                    ...search.getUsedFiltersSummary()
                }
            });
        } catch (err) {
            console.error(err);
        }
    }

    openCreateDialog(search: TargetingSearch, onResultPage: boolean = false) {
        const io: NewResourceDialogModel = {
            inputs: {
                heading: 'Discover.dialogs.saveSearch.heading',
                description: 'Discover.dialogs.saveSearch.description',
                placeholder: 'Discover.dialogs.saveSearch.placeholder',
                okLabel: 'Discover.dialogs.saveSearch.save',
                cancelLabel: 'Discover.dialogs.universal.cancelLabel',
                currentGroup: search.displayGroupId,
                width: 300,
                observableFormValidator: (searchName: string) => {
                    return this.isNameDuplicated$(searchName).pipe(
                        map((isDuplicate) => {
                            if (isDuplicate) {
                                return ['Discover.dialogs.saveSearch.duplicate'];
                            } else {
                                return [];
                            }
                        })
                    );
                },
                submit: (searchName: string, group?: DisplayGroupModel) => {
                    search.name = searchName;
                    search.displayGroupId = group?.id;

                    this.trackSearchEvent(TargetingSearchPendoEventEnum.SAVE, search);

                    return this.create(search).pipe(
                        tap((id) => {
                            this.setCurrentResource(search);

                            if (onResultPage) {
                                this.discoverRoutingService.gotoTargetingSearchResults(id);
                            } else {
                                this.discoverRoutingService.gotoTargetingSearch(id);
                            }
                        }),
                    );
                }
            },
            outputs: {}
        };
        this.coreDialogService.open(NewResourceDialogComponent, io);
    }

    update(search: TargetingSearch): Observable<number> {
        this.loadingSubject.next(true);
        return this.updateResource(search, { actionType: ResourceActionTypes.UPDATE }).pipe(
            tap(() => {
                this.cachedResource = search.clone();
                this.currentResourceModified();
                this.unavailableOutletSubject.next(false);
            }),
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            }),
            finalize(() => this.loadingSubject.next(false))
        );
    }

    /** Update the hidden state of the specified search (via id) and force refresh of all$ before returning searchId. */
    updateVisibility(searchId: number, action: ActionsEnum): Observable<number> {
        return this.getById(searchId).pipe(
            switchMap((search) => {
                search.hidden = action === ActionsEnum.hide;
                return this.updateResource(search, { actionType: ResourceActionTypes.UPDATE }, false);
            }),
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            })
        );
    }

    /** Update the hidden state of the specified searches and force refresh of all$ before returning search ids. */
    bulkUpdateVisibility(searchIds: number[], action: ActionsEnum): Observable<number[]> {
        return forkJoin(searchIds.map(id => this.getById(id))).pipe(
            switchMap((searches: TargetingSearch[]) => {
                searches.forEach(list => list.hidden = action === ActionsEnum.hide);
                return this.updateBulkResources(searches);
            }),
            switchMap((ids) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => ids)
                );
            })
        );
    }

    /** Check for duplicates, then rename the specified targeting search */
    renameListDupCheck(id: number, name: string): Observable<number> {
        return this.isNameDuplicated$(name, id).pipe(
            switchMap((duplicate) => {
                if (duplicate) {
                    this.messageService.add({
                        severity : messageSeverityEnum.error,
                        summary: 'Discover.dialogs.renameSearch.error.summary',
                        detail: 'Discover.dialogs.saveSearch.duplicate'
                    });
                    throw new Error(CommonApiErrorEnum.duplicateName);
                } else {
                    return this.rename(id, name);
                }
            })
        );
    }

    /**
     * Rename the specified search (via id) with the new name and force refresh of all$ before returning searchId.
     * If the currentResource is the search being renamed, force a refresh of the currentResource.
     * @param searchId - TargetingSearch id that is being renamed
     * @param name - the new updated name property
     */
    rename(searchId: number, name: string): Observable<number> {
        return this.currentResource$.pipe(
            take(1),
            switchMap((focusedSearch) => {
                return this.getById(searchId).pipe(
                    switchMap((list) => {
                        list.name = name;
                        return this.updateResource(list, {
                            actionType: ResourceActionTypes.RENAME,
                            refreshResource: focusedSearch?.id === searchId,
                        });
                    }),
                    switchMap((id) => {
                        return this.refreshAllManual().pipe(
                            filter(x => !!x),
                            take(1),
                            map(x => id)
                        );
                    })
                );
            })
        );
    }

    /**
     * Delete the specified searchId and force refresh of all$ before returning id.
     * If the currentResource is the search being deleted, nav to hype screen & reset currentResource to null.
     * @param searchId - number
     */
    delete(searchId: number): Observable<number> {
        return this.currentResource$.pipe(
            take(1),
            switchMap((focusedSearch) => {
                return this.deleteResource(searchId, {
                    actionType: ResourceActionTypes.DELETE,
                    resetResource: focusedSearch?.id === searchId,
                }).pipe(
                    tap(() => {
                        if (focusedSearch?.id === searchId) {
                            this.discoverRoutingService.gotoTargetingHype();
                        }
                    })
                );
            }),
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            }),
        );
    }

    /**
     * Similar logic is used across different services.
     *
     * @TODO Consider consolidating repeated bulk deletion logic using shared CRUD interfaces.
     */
    confirmBulkDelete(items: AuditableModel[], groups: DisplayGroupModel[]): Observable<boolean> {
        const list = items.map(item => item.name);
        const secondaryList = groups.map(item => item.name);

        return new Observable<boolean>((subscriber) => {
            let isListSelected: boolean = true;
            let isSecondaryListSelected: boolean = false;

            const confirmConfig: ConfirmDialogModel = {
                inputs: {
                    id: 'bulkDeleteSearch',
                    heading: 'Discover.confirmDialog.bulkDeleteSearch.heading',
                    message: 'Common.confirmDialog.message',
                    list,
                    listLabel: 'Discover.confirmDialog.bulkDeleteSearch.checkboxLabel',
                    isListSelected,
                    secondaryList,
                    secondaryListLabel: 'Common.confirmDialog.checkboxLabel.groups',
                    isSecondaryListSelected,
                    okLabel: 'Core.button.delete'
                },
                outputs: {
                    listSelectionChanged: (isSelected: boolean) => {
                        isListSelected = isSelected;
                    },
                    secondaryListSelectionChanged: (isSelected: boolean) => {
                        isSecondaryListSelected = isSelected;
                    },
                    dialogClose: (confirmed: any) => {
                        if (confirmed) {
                            if (isListSelected) {
                                const ids = items.map(item => item.id);
                                this.bulkDelete(ids).subscribe();
                            }

                            if (isSecondaryListSelected) {
                                groups.forEach(group => this.displayGroupsService.deleteGroup(group));
                            }
                        }
                        subscriber.next(confirmed);
                        subscriber.complete();
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
        });
    }

    private bulkDelete(ids: number[]): Observable<number[]> {
        return this.currentResource$.pipe(
            take(1),
            switchMap((focusedSearch) => {
                const action = {
                    actionType: ResourceActionTypes.BULK_DELETE,
                    resetResource: ids.includes(focusedSearch?.id),
                    count: ids.length
                };

                return this.bulkDeleteResourceByIds(ids, action).pipe(
                    tap(() => {
                        if (ids.includes(focusedSearch?.id)) {
                            this.discoverRoutingService.gotoTargetingHype();
                        }
                    })
                );
            }),
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            })
        );
    }

    getFilterListItems(): Observable<AuditableModel[]> {
        return this.all$;
    }


    private createBlankSearch(userSearchSettings: DisplaySettings) {
        const search = new TargetingSearch();
        if (userSearchSettings) {
            search.languages = new IncludeExcludeModel(userSearchSettings.languages);
            search.geographies = new IncludeExcludeModel(userSearchSettings.locations);
            search.includePrivateContacts = userSearchSettings.includePrivateContacts;
        }

        search.searchMode = SearchModeEnum.contact;

        this.setCurrentResource(search);
        this.previewCountLoading$.next(false);
    }

    private mapContactOutletIdsToNames(search: TargetingSearch): Observable<TargetingSearch> {
        const requests: Observable<string>[] = [];
        const unavailableOutlets: string[] = [];
        this.updateUnavailableOutletState(unavailableOutlets);

        search?.contactIdsNames?.include?.forEach(contact => {
            if (contact.id && !contact.name) {
                requests.push(this.contactApi.getContact(contact.id).pipe(
                    map(result => contact.name = result.firstName + ' ' + result.lastName)));
            }
        });

        search?.contactIdsNames?.exclude?.forEach(contact => {
            if (contact.id && !contact.name) {
                requests.push(this.contactApi.getContact(contact.id).pipe(
                    map(result => contact.name = result.firstName + ' ' + result.lastName)));
            }
        });

        search?.outletIdsNames?.include?.forEach(outlet => {
            if (outlet.id && !outlet.name) {
                const request = this.outletApi.getOutlet(outlet.id).pipe(
                    map(result => {
                        outlet.name = result.name;
                        return outlet.name;
                    }),
                    catchError(() => {
                        unavailableOutlets.push(outlet.id);
                        this.updateUnavailableOutletState(unavailableOutlets);
                        return of('');
                    })
                );

                requests.push(request);
            }
        });

        search?.outletIdsNames?.exclude?.forEach(outlet => {
            if (outlet.id && !outlet.name) {
                requests.push(this.outletApi.getOutlet(outlet.id).pipe(map(result => outlet.name = result.name)));
            }
        });

        if (requests.length === 0) {
            return of(search);
        }

        return forkJoin(requests).pipe(map(() => {
            return search;
        }));
    }

    updateUnavailableOutletState(unavailableOutlet: string[]): void {
        this.unavailableOutletSubject.next(unavailableOutlet.length > 0);
    }

    getValidationErrors(search: TargetingSearch, isSearchResultPage: boolean): Observable<{
        hasNoSearchName: boolean;
        searchNameDuplicate: boolean;
    }> {
        const errors = {
            hasNoSearchName: false,
            searchNameDuplicate: false
        };

        return this.isNameDuplicated$(search.name, search.id).pipe(
            tap(duplicate => {
                errors.hasNoSearchName = !(search.name && search.name.length > 0);
                errors.searchNameDuplicate = duplicate;

                if (isSearchResultPage) {
                    if (errors.hasNoSearchName) {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'Discover.targeting.errors.saveSearchErrorSummary',
                            detail: 'Discover.targeting.errors.emptyName'
                        });
                    } else if (errors.searchNameDuplicate) {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'Discover.targeting.errors.saveSearchErrorSummary',
                            detail: 'Discover.dialogs.saveSearch.duplicate'
                        });
                    }
                }
            }),
            map(() => errors)
        );
    }

    openCopyTargetingSearchDialog(searchList: TargetingSearchList) {
        const io: NewResourceDialogModel = {
            inputs: {
                heading: 'Discover.copySearch',
                initialValue: searchList.name + this.localizationService.transform('Common.dialog.newResource.copy.suffix'),
                currentGroup: searchList.groupId,
                okLabel: 'Common.dialog.newResource.copy',
                observableFormValidator: (searchName: string) => {
                    return this.isNameDuplicated$(searchName).pipe(
                        map((isDuplicate) => {
                            if (isDuplicate) {
                                return ['Discover.searchNameAlreadyExists'];
                            } else {
                                return [];
                            }
                        })
                    );
                },
                submit: (name: string, group?: DisplayGroupModel) => {
                    return this.getById(searchList.id).pipe(
                        switchMap((search) => {
                            const newSearch = search.clone();
                            newSearch.name = name;
                            newSearch.displayGroupId = group?.id;
                            newSearch.isFavorited = false;
                            delete newSearch.id;

                            return this.createResource(newSearch, { actionType: ResourceActionTypes.COPY });
                        }),
                        tap((id) => this.discoverRoutingService.gotoTargetingSavedSearch(id)),
                        switchMap(() => {
                            return this.refreshAllManual().pipe(
                                filter(x => !!x),
                                take(1),
                            );
                        })
                    );
                }
            }
        };
        this.coreDialogService.open(NewResourceDialogComponent, io);
    }

    /**
     * Adds provided searchs to a group (or removes group ID if Uncategorized is selected)
     * @param searchIds
     * @param group undefined if Uncategorized is selected
     * @param refreshAll: boolean = false
     */
    updateGroup(searchIds: number[], group: DisplayGroupModel, refreshAll: boolean = false): Observable<TargetingSearchList> {
        let request: Observable<void>;
        if (group?.id) {
            request = this.targetingSearchesApiService.updateDisplayGroup(searchIds, [], group.id);
        } else {
            request = this.targetingSearchesApiService.removeDisplayGroup(searchIds);
        }

        return request.pipe(
            catchError(() => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Common.lhp.moveToGroup.error.summary',
                    detail: 'Common.lhp.moveToGroup.error.detail'
                });

                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1)
                );
            }),
            switchMap(() =>
                iif(
                    () => refreshAll,
                    this.refreshAllManual().pipe(
                        filter(x => !!x),
                        take(1),
                    ),
                    of(null)
                )
            ),
            switchMap(() =>
                iif(
                    () => searchIds.length === 1,
                    this.all$.pipe(
                        take(1),
                        map(searches => searches.find(search => search.id === searchIds[0])),
                    ),
                    of(null)
                )
            ),
            tap((search: TargetingSearchList) => {
                if (search) {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.lhp.moveToGroup.success.summary',
                        detail: this.localizationService.transform('Common.lhp.moveToGroup.success.detail.single',
                            { itemName: search?.name, groupName: group.name }
                        )
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.lhp.moveToGroup.success.summary',
                        detail: this.localizationService.transform('Common.lhp.moveToGroup.success.detail.multiple',
                            { number: searchIds.length, name: group.name }
                        )
                    });
                }
            })
        );
    }

    /**
     * Removes the provided searches from all display groups & refresh all$
     */
    removeSearchFromGroup(searchIds: number[]): Observable<null> {
        return this.targetingSearchesApiService.removeDisplayGroup(searchIds).pipe(
            switchMap(() => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => null)
                );
            })
        );
    }


    updateFavorites(addIds: number[], removeIds: number[]): Observable<void> {
        return this.targetingSearchesApiService.updateFavorites(addIds, removeIds).pipe(
            catchError((error) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Common.lhp.moveToGroup.error.summary',
                    detail: 'Common.lhp.moveToGroup.error.detail'
                });

                throw new Error(error);
            }),
            switchMap(() => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => null)
                );
            }),
        );
    }


    getSearchCount(searchId: number, searchFilter: TargetingSearch): Observable<number> {
        const filterApiModel = !!searchFilter ? searchFilter.toApiModel().query : null;

        return this.targetingSearchesApiService.getSearchCount(searchId, filterApiModel);
    }


    fetchPreviewCount(search: TargetingSearch) {
        if (!search || !search.hasAnyFilters(search.searchMode)) {
            this.previewCount$.next(null);
            return;
        }
        this.previewCountLoading$.next(true);

        const query = search.toApiModel(false, null).query;
        this.targetingSearchesApiService.performSearch(query, 0, 0)
            .pipe(take(1))
            .subscribe({
                next: (x) => {
                    this.previewCount$.next(x.numResults);
                    this.previewCountLoading$.next(false);
                },
                error: () => {
                    this.previewCountLoading$.next(false);
                }
            });
    }

    getPreviewCountLoading(): Observable<boolean> {
        return this.previewCountLoading$;
    }

    getPreviewCount(): Observable<number> {
        return this.previewCount$;
    }

    getLoading(): Observable<boolean> {
        return this.loading$;
    }

    outletSearcher(query: string): Observable<OutletModel[]> {
        return this.journodexApiService.performQuickSearchOutlets(query).pipe(map(results => {
            return results.map(result => {
                const model: OutletModel = this.journodexEntityFactory.createEmptyOutlet();
                model.id = result.id;
                model.name = result.entityName;
                model.avatarUrl = result.avatarUrl;
                model.location = result.location;
                return model;
            });
        }));
    }

    contactSearcher(query: string): Observable<ContactModel[]> {
        return this.journodexApiService.performQuickSearchContacts(query).pipe(map(results => {
            return results.map(result => {
                const model: ContactModel = this.journodexEntityFactory.createEmptyContact();
                model.id = result.id;
                model.name = result.entityName;
                model.avatarUrl = result.avatarUrl;
                model.outlet = result.outlet;
                return model;
            });
        }));
    }


    /**
     * Converts possible parent-only (old data) includeExcludeModels to current child-only models.
     */
    private getAllChildrenSubjectsOfIncludeExclude(subjects: IncludeExcludeModel<number>): Observable<IncludeExcludeModel<number>> {
        if (!subjects) {
            return of(null);
        }

        return forkJoin([
            this.journodexSubjectsService.getChildrenByIds(subjects.include),
            this.journodexSubjectsService.getChildrenByIds(subjects.includeAll),
            this.journodexSubjectsService.getChildrenByIds(subjects.exclude)
        ]).pipe(
            map(([include, includeAll, exclude]) => {
                const subjectsIncludeExclude = new IncludeExcludeModel<number>();
                subjectsIncludeExclude.include = include.map(child => child.id);
                subjectsIncludeExclude.includeAll = includeAll.map(child => child.id);
                subjectsIncludeExclude.exclude = exclude.map(child => child.id);
                return subjectsIncludeExclude;
            })
        );
    }


    public getRecommendedRecipientsQueryByText(text: string): Observable<boolean> {
        return this.distributionApi.getRecommendedRecipientsQueryByText(text)
            .pipe(
                filter(apiModel => !!apiModel),
                map (apiModel => {
                    apiModel.searchMode = SearchModeEnum.contact;
                    const newSearch = new TargetingSearch({ query: apiModel });
                    this.journodexEnumService.filterOutInvalidReferenceData(newSearch);
                    const empty = newSearch.hasEmptyQuery();
                    if (!empty) {
                        this.setCurrentResource(newSearch);
                        this.targetingSearchService.setActiveSearch(newSearch);
                    }

                    return !empty;
                })
            );
    }

    public getRecommendedRecipientsQueryByDistribution(id: number): Observable<boolean> {
        return this.distributionApi.getRecommendedRecipientsQueryByDistribution(id)
            .pipe(
                filter(apiModel => !!apiModel),
                map(apiModel => {
                    apiModel.searchMode = SearchModeEnum.contact;
                    const newSearch = new TargetingSearch({ query: apiModel });
                    const empty = newSearch.hasEmptyQuery();
                    if (!empty) {
                        this.setCurrentResource(newSearch);
                    }
                    return !empty;
                })
            );
    }

    getDistributionSummaries() {
        return this.distributions$;
    }

    private setAndPerformParamSearch(searchName: string, searchMode: SearchModeEnum) {
        const search = new TargetingSearch();
        search.searchMode = searchMode;

        if (searchMode === SearchModeEnum.contact) {
            search.contactIdsNames.include = [new IdNamePairModel(searchName)];
        } else {
            search.outletIdsNames.include = [new IdNamePairModel(searchName)];
        }

        this.targetingSearchService.performSearch(search);

        this.setCurrentResource(search);
    }

    private setCurrentResourceById(searchId: number, performSearch: boolean): Observable<null> {
        return this.currentResource$.pipe(
            take(1),
            switchMap(focusedSearch => {
                if (!focusedSearch || focusedSearch.id !== searchId) {
                    return this.getById(searchId);
                } else {
                    if (performSearch) {
                        this.targetingSearchService.performSearch(focusedSearch);
                    }

                    return EMPTY;
                }
            }),
            catchError((error) => {
                if (error.status === 404) {
                    const io = {
                        inputs: {
                            heading:'Monitor.results.savedSearchNotFoundDialog.heading',
                            message: 'Monitor.results.savedSearchNotFoundDialog.message',
                            okLabel:'Monitor.results.savedSearchNotFoundDialog.acknowledge',
                            showCancel:false,
                            confirmAction: () => {
                                this.discoverRoutingService.gotoTargetingHype();
                                return of(true);
                            }
                        }
                    };

                    this.coreDialogService.open(ConfirmDialogComponent, io);
                }
                return throwError(() => new Error('Targeting List not found'));
            }),
            tap((resource) => {
                this.setCurrentResource(resource);
                if (performSearch) {
                    this.targetingSearchService.performSearch(resource);
                }
            }),
            map(() => null)
        );
    }

    private onUpdateRoute(route: DiscoverRouteModel, searchSettings: DisplaySettings): Observable<null> {
        this.currentRoute = route;

        const id: number = +route.value;
        const type: DiscoverRouteTypeEnum = route.type;

        switch (type) {
            case DiscoverRouteTypeEnum.targetingSavedSearchResults: {
                return this.setCurrentResourceById(id, true)
                .pipe(tap(() => this.initFiltersFromRoute(route)));
            }
            case DiscoverRouteTypeEnum.targetingSavedSearch: {
                return this.setCurrentResourceById(id, false);
            }
            case DiscoverRouteTypeEnum.privateContacts: {
                const privateContactSearch = new TargetingSearch();
                privateContactSearch.searchMode = SearchModeEnum.contact;
                privateContactSearch.privateContactsOnly = true;

                this.targetingSearchService.performSearch(privateContactSearch);

                return of(null)
                .pipe(tap(() => this.initFiltersFromRoute(route)));
            }
            case DiscoverRouteTypeEnum.targetingResults: {
                return this.currentResource$.pipe(
                    take(1),
                    switchMap(focusedSearch => {
                        if (focusedSearch) {
                            this.targetingSearchService.performSearch(focusedSearch);
                        } else {
                            const params: ParamMap = this.currentRoute.queryParamMap;
                            const searchName = params?.get('name');
                            const searchType = params?.get('type');

                            if (searchName && searchType) {
                                if (searchType === EntityType.CONTACT) {
                                    this.setAndPerformParamSearch(searchName, SearchModeEnum.contact);
                                    return of(null);
                                } else if (searchType === EntityType.OUTLET) {
                                    this.setAndPerformParamSearch(searchName, SearchModeEnum.outlet);
                                    return of(null);
                                }
                            }

                            // reachable if this is the first page accessed on app load
                            if (params) {
                                this.discoverRoutingService.gotoTargetingSearch();
                            }
                        }

                        return of(null);
                    })
                );
            }
            case DiscoverRouteTypeEnum.targetingSearch: {
                // if filters in the url params
                const search = TargetingSearch.constructFromRouteParam(route?.queryParamMap);
                if (search) {
                    search.searchMode = SearchModeEnum.contact;
                    this.setCurrentResource(search);

                // if copy in the url params
                } else if (route.queryParamMap && route.queryParamMap.has('copy')) {

                    // get target search by the ID in the url params
                    return this.getById(+route.queryParamMap.get('copy')).pipe(
                        map(search => {
                            search.id = null;
                            search.name = search.name + ' [CLONE]';
                            this.creatingNewSearch = true;

                            return search;
                        }),
                        tap(search => this.setCurrentResource(search)),
                        map(() => null),
                        catchError(() => {
                            // id no longer exists, go to blank search
                            this.createBlankSearch(searchSettings);
                            return of(null);
                        })
                    );
                } else {
                    // if no clone param in the url, blank search
                    this.createBlankSearch(searchSettings);
                }

                this.previewCount$.next(null);

                return of(null);
            }
            case DiscoverRouteTypeEnum.targetingSearchUpdate: {
                // reachable if this is the first page accessed on app load
                // since unsaved search will be cleared, navigate to search form
                return this.currentResource$.pipe(
                    take(1),
                    switchMap((focusedSearch) => {
                        if (!focusedSearch) {
                            this.discoverRoutingService.gotoTargetingSearch();
                        }

                        return of(null);
                    })
                );
            }
            default:
            case DiscoverRouteTypeEnum.targetingSearchHype: {
                return of(null);
            }
        }
    }

    private initFiltersFromRoute(route: DiscoverRouteModel) {
        const search = TargetingSearch.constructFromRouteParam(route?.queryParamMap);
        if (search) {
            this.targetingSearchService.applyInitFiltersOnListReady(search);
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
